import { Directive, inject, input, model } from '@angular/core';
import { BaseComponent, EventBusService, WindowService } from '@ups/xplat/core';
import { filter, takeUntil } from 'rxjs';

export interface HeaderTitleOptions {
  title: string;
  icon?: string;
  tab?: string;
}

@Directive()
export abstract class HeaderBaseComponent extends BaseComponent {
  win = inject(WindowService);
  readonly title = input('');
  readonly showLogo = input(false);
  readonly showSettings = input(false);
  readonly listenForHeaderUpdates = input(true);
  /**
   * Optional page/tab identity this header belongs to. When set, the header
   * accepts a `headerUpdateTitle` broadcast ONLY when the broadcast is addressed
   * to the same scope (its `tab` equals this value). This stops sibling pages
   * that are alive at the same time — e.g. every page in a bottom TabView, all
   * mounted at once — from overwriting each other's title. Leave unset (the
   * default) for a lone header that should track every broadcast, preserving
   * existing behavior everywhere else.
   */
  readonly headerScope = input<string | null | undefined>(undefined);
  readonly logoSrc = input('');
  readonly logoAltText = input('');
  readonly breadcrumbs = input<
    Array<{ name?: string; path?: string; disabled?: boolean }>
  >([
    {
      name: 'Home',
      path: '/home',
    },
    {
      name: 'Section',
      path: '#',
    },
    {
      name: 'Subsection',
      path: '#',
    },
    {
      name: 'Page',
      disabled: true,
    },
  ]);

  /**
   * Current header title/icon. Bindable as an input (`[options]="..."`) and also
   * written internally from the `headerUpdateTitle` event bus, so both paths
   * funnel through one writable signal (replaces the former `options$`
   * BehaviorSubject).
   */
  readonly options = model<HeaderTitleOptions | null>(null);

  constructor(protected eventBus: EventBusService) {
    super();
    this.eventBus
      .observe<HeaderTitleOptions>(this.eventBus.types.headerUpdateTitle)
      .pipe(
        // update header title with exception cases
        filter(
          () => this.listenForHeaderUpdates() && !!this.win.headerUpdatesEnabled
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((options) => {
        // A scoped header ignores broadcasts addressed to a different page so
        // sibling tabs kept alive at the same time can't clobber it.
        if (this.matchesScope(options)) {
          this.options.set(options);
        }
      });
  }

  /**
   * A scoped header (see {@link headerScope}) accepts a broadcast only when it
   * is addressed to that scope via the `tab` field. An unscoped header accepts
   * every broadcast.
   */
  protected matchesScope(
    options: { tab?: string } | null | undefined
  ): boolean {
    if (!this.headerScope()) {
      return true;
    }
    return !!options && options.tab === this.headerScope();
  }
}
